import { Configuration, RetailersApi } from '@/api';
import useGetRetailerQuery from '@/api/queries/useGetRetailerQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { RetailerCreate, RetailerUpdate } from '@/models/Retailer';
import Breadcrumbs from '@/modules/admin/components/Breadcrumbs';
import RetailerForm from '@/modules/admin/components/RetailerForm';
import { RetailerListPath, RetailerStoreListPath } from '@/modules/admin/paths';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { BuildingStorefrontIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const RetailerEdit = () => {
  const navigate = useNavigate();

  const { retailerId } = useParams();

  const retailersApi = new RetailersApi(new Configuration(useAuth));

  const { isLoading, retailer, invalidateQuery } = useGetRetailerQuery(retailerId);

  function isRetailerUpdate(
    retailerData: RetailerCreate | RetailerUpdate,
  ): retailerData is RetailerUpdate {
    return 'id' in retailerData;
  }

  const handleOnSubmit = async (retailerData: RetailerCreate | RetailerUpdate): Promise<void> => {
    if (!isRetailerUpdate(retailerData)) {
      return;
    }

    try {
      await retailersApi.updateRetailer(retailerData.id, retailerData);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Retailer "${retailerData.name}" updated successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();

      navigate(RetailerListPath);
    } catch (error) {
      console.error('Failed to update retailer:', error);
    }
  };

  const handleOnDelete = async () => {
    if (!retailer?.id) {
      return;
    }

    try {
      await retailersApi.deleteRetailer(retailer.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Retailer "${retailer.name}" deleted successfully!`}
          visible={t.visible}
        />
      ));

      navigate(RetailerListPath);
    } catch (error) {
      console.error('Failed to delete retailer:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!retailer) {
    return;
  }

  return (
    <>
      <Breadcrumbs />

      <PageHeader title="Edit Retailer">
        <Button
          to={RetailerStoreListPath.withRetailerId(retailer.id)}
          icon={<BuildingStorefrontIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
          variant="secondary"
        >
          Stores
        </Button>
        <Confirm
          onConfirm={handleOnDelete}
          title="Delete Retailer"
          message={`Are you sure you want to delete retailer ${retailer.name}? This action cannot be undone.`}
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete Retailer
            </Button>
          )}
        </Confirm>
      </PageHeader>

      <div className="w-full bg-white mx-auto rounded-lg shadow p-6 lg:p-12">
        <RetailerForm onSubmit={handleOnSubmit} retailer={retailer} />
      </div>
    </>
  );
};

export default RetailerEdit;
